import {
  getAnswerReferenceByAccessor,
  parseAnswerReferenceAccessor,
  type AnswerReferenceAccessor,
} from "./answerReferenceRegistry";
import { ANSWER_REFERENCE_PATTERN } from "./referenceTokens";
import type { InterviewItemTemplateId } from "@/components/interview/types";

export const formatPillLabel = (
  questionNumber: number | undefined,
  accessor?: AnswerReferenceAccessor
): string => {
  if (questionNumber === undefined) return "[invalid reference]";
  const def = getAnswerReferenceByAccessor(accessor);
  return `Q${questionNumber} ${def?.label ?? "answer"}`;
};

export type AnswerReferenceSegment =
  | { type: "text"; value: string }
  | {
      type: "pill";
      questionNumber: number | undefined;
      templateId: InterviewItemTemplateId;
      accessor: AnswerReferenceAccessor | undefined;
    };

export const parseAnswerReferences = (
  text: string,
  templateNumbers: Map<InterviewItemTemplateId, number> | undefined
): AnswerReferenceSegment[] => {
  const segments: AnswerReferenceSegment[] = [];
  const pattern = new RegExp(ANSWER_REFERENCE_PATTERN.source, "g");

  let lastIndex = 0;
  let match;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push({
        type: "text",
        value: text.slice(lastIndex, match.index),
      });
    }

    const templateId = match[1] as InterviewItemTemplateId;
    const accessor = parseAnswerReferenceAccessor(match[2]);
    segments.push({
      type: "pill",
      questionNumber: templateNumbers?.get(templateId),
      templateId,
      accessor,
    });

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    segments.push({ type: "text", value: text.slice(lastIndex) });
  }

  return segments.length > 0 ? segments : [{ type: "text", value: text }];
};

export const segmentsToSpokenText = (
  segments: AnswerReferenceSegment[]
): string =>
  segments
    .map((seg) => {
      if (seg.type === "text") return seg.value;
      const qNum = seg.questionNumber;
      if (qNum === undefined) return "your previous answer";
      return (
        getAnswerReferenceByAccessor(seg.accessor)?.spoken(qNum) ??
        "your previous answer"
      );
    })
    .join("");

export const formatTextWithPills = (
  text: string,
  templateNumbers: Map<InterviewItemTemplateId, number> | undefined
): string =>
  text.replace(
    ANSWER_REFERENCE_PATTERN,
    (_, templateId: string, rawAccessor: string | undefined) => {
      const questionNumber = templateNumbers?.get(
        templateId as InterviewItemTemplateId
      );
      const accessor = parseAnswerReferenceAccessor(rawAccessor);
      const label = formatPillLabel(questionNumber, accessor);
      return `<answer-pill>${label}</answer-pill>`;
    }
  );

export const formatTextWithAnswerReferenceLabels = (
  text: string,
  templateNumbers: Map<InterviewItemTemplateId, number> | undefined
): string =>
  text.replace(
    ANSWER_REFERENCE_PATTERN,
    (_, templateId: string, rawAccessor: string | undefined) => {
      const questionNumber = templateNumbers?.get(
        templateId as InterviewItemTemplateId
      );
      const accessor = parseAnswerReferenceAccessor(rawAccessor);
      return `[${formatPillLabel(questionNumber, accessor)}]`;
    }
  );

// Replaces {{answer:UUID}} references with a readable marker like {{Answer to Q3}}.
// Used by static exports (Word, CSV) where the live pill UI is unavailable.
export const formatAnswerReferencesForExport = (
  text: string,
  templateNumbers: Map<InterviewItemTemplateId, number> | undefined
): string =>
  text.replace(
    ANSWER_REFERENCE_PATTERN,
    (match, templateId: string, rawAccessor: string | undefined) => {
      const questionNumber = templateNumbers?.get(
        templateId as InterviewItemTemplateId
      );
      if (questionNumber === undefined) return match;
      const accessor = parseAnswerReferenceAccessor(rawAccessor);
      const def = getAnswerReferenceByAccessor(accessor);
      return def?.accessor
        ? `{{Answer to Q${questionNumber}: ${def.label}}}`
        : `{{Answer to Q${questionNumber}}}`;
    }
  );
